import { useRef } from 'react';
import { Upload, Trash2, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { Block, HeaderBlockContent } from '@/types';

interface HeaderBlockProps {
  block: Block;
  isEditable?: boolean;
  onUpdate?: (patch: Partial<Block>) => void;
}

function HeaderBlock({ block, isEditable = false, onUpdate }: HeaderBlockProps) {
  const content = block.content as HeaderBlockContent;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const alignment = content.alignment || 'left';
  const showLogo = content.showLogo !== false;

  const handleLogoUpload = (file: File) => {
    if (!file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      onUpdate?.({
        content: { ...content, logoUrl: e.target?.result as string },
      });
    };
    reader.readAsDataURL(file);
  };

  if (isEditable) {
    return (
      <div className="rounded-lg border p-4">
        <div className="mb-4 flex justify-between items-center">
          <span className="text-xs font-medium text-muted-foreground">Briefhoofd / Header</span>
          <div className="inline-flex rounded-md border">
            <button
              className={`p-1.5 ${alignment === 'left' ? 'bg-accent' : ''}`}
              onClick={() => onUpdate?.({ content: { ...content, alignment: 'left' } })}
            >
              <AlignLeft className="h-4 w-4" />
            </button>
            <button
              className={`p-1.5 ${alignment === 'center' ? 'bg-accent' : ''}`}
              onClick={() => onUpdate?.({ content: { ...content, alignment: 'center' } })}
            >
              <AlignCenter className="h-4 w-4" />
            </button>
            <button
              className={`p-1.5 ${alignment === 'right' ? 'bg-accent' : ''}`}
              onClick={() => onUpdate?.({ content: { ...content, alignment: 'right' } })}
            >
              <AlignRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div
          className={`flex gap-6 ${
            alignment === 'center' ? 'flex-col items-center' : 'flex-row items-start'
          }`}
          style={{ textAlign: alignment }}
        >
          {/* Logo section */}
          {showLogo && (
            <div className="shrink-0">
              {content.logoUrl ? (
                <div className="relative">
                  <img
                    src={content.logoUrl}
                    alt="Logo"
                    style={{ maxWidth: 180, maxHeight: 80, objectFit: 'contain' }}
                  />
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => fileInputRef.current?.click()}>
                      Vervangen
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="text-destructive"
                      onClick={() => onUpdate?.({ content: { ...content, logoUrl: undefined } })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className="w-[180px] h-[80px] border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-5 w-5 text-gray-400" />
                  <span className="text-xs text-muted-foreground">Logo uploaden</span>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleLogoUpload(file);
                }}
              />
            </div>
          )}

          {/* Company info */}
          <div className="flex-1 flex flex-col gap-2">
            <Input
              placeholder="Bedrijfsnaam"
              value={content.companyName || ''}
              onChange={(e) => onUpdate?.({ content: { ...content, companyName: e.target.value } })}
              className="h-8"
            />
            <textarea
              placeholder="Adres"
              value={content.companyAddress || ''}
              onChange={(e) => onUpdate?.({ content: { ...content, companyAddress: e.target.value } })}
              rows={2}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <div className="flex gap-2">
              <Input
                placeholder="Telefoon"
                value={content.companyPhone || ''}
                onChange={(e) => onUpdate?.({ content: { ...content, companyPhone: e.target.value } })}
                className="h-8"
              />
              <Input
                placeholder="E-mail"
                value={content.companyEmail || ''}
                onChange={(e) => onUpdate?.({ content: { ...content, companyEmail: e.target.value } })}
                className="h-8"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Read-only view
  return (
    <div
      className={`flex gap-6 py-4 border-b border-gray-200 mb-4 ${
        alignment === 'center' ? 'flex-col items-center' : 'flex-row items-start'
      }`}
      style={{ textAlign: alignment }}
    >
      {showLogo && content.logoUrl && (
        <img
          src={content.logoUrl}
          alt="Logo"
          style={{ maxWidth: 180, maxHeight: 80, objectFit: 'contain' }}
        />
      )}

      <div className="flex-1">
        {content.companyName && (
          <h6 className="text-base font-bold">{content.companyName}</h6>
        )}
        {content.companyAddress && (
          <p className="text-sm text-muted-foreground whitespace-pre-line">
            {content.companyAddress}
          </p>
        )}
        <div className="flex gap-4 mt-1 flex-wrap" style={{ justifyContent: alignment }}>
          {content.companyPhone && (
            <span className="text-sm text-muted-foreground">
              {content.companyPhone}
            </span>
          )}
          {content.companyEmail && (
            <span className="text-sm text-muted-foreground">
              {content.companyEmail}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default HeaderBlock;
